import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfirmProvider } from './components/common/ConfirmProvider';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { RouteError } from './components/common/RouteError';
import { Toaster } from './components/ui/sonner';
import { trpc, trpcClient } from './lib/trpc';
import { routeTree } from './routeTree.gen';
import '@fontsource-variable/geist';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteError,
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <ConfirmProvider>
            <RouterProvider router={router} />
            <Toaster
              closeButton
              position="bottom-center"
              richColors
              // Keep toasts above the mobile bottom tab bar
              mobileOffset={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
            />
          </ConfirmProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  </StrictMode>,
);
